import type { Group, SceneControl } from '@anov/3d-core'
import { Clock, LoaderModule, Material, Mesh } from '@anov/3d-core'
import type { ModelOption as LinkOption } from '../types'
import { SOURCE_BASE_PATH } from '../../Utils/constant'
import { EMarkAnimationType, KeyframeAnimation } from '../../Mark/KeyframeAnimation'
import LinkMesh from '.'

const MODEL_URI = `${SOURCE_BASE_PATH}/element/pyramid_1.glb`
const load = new LoaderModule()

class PyramidMesh extends LinkMesh {
  private _color: string
  private _animationFrameId?: number
  private _disposeCallbacks: Function[] = []
  // 动画相关参数
  private _animationController: KeyframeAnimation

  public get color(): string {
    return this._color
  }

  public set color(newValue: string) {
    if (this._color === newValue)
      return
    this._color = newValue
    this.setModelColor()
  }

  constructor(option: LinkOption, sceneControl: SceneControl) {
    super(sceneControl)
    this._vspace = [0.4, 0]
    const { color } = option
    this._color = color
    this._readyPromise = this.init()
  }

  dispose() {
    this._disposeCallbacks.forEach(fn => fn())
    this._disposeCallbacks = []
    this.remove(this._mesh)
  }

  private async init() {
    this._mesh = await this.loadModel(MODEL_URI) as Group
    this.setModelColor()
    this.add(this._mesh)

    this._animationController = new KeyframeAnimation(this._mesh, {
      enable: true,
      type: [EMarkAnimationType.Rotate, EMarkAnimationType.Bounce],
      duration: 4,
    })
    this._animationController.play()
    return true
  }

  private loadModel(uri: string) {
    return new Promise((resolve, reject) => {
      load.loadGLTF({
        url: uri,
        autoBackward: false,
        isCache: true,
        draco: true,
        onLoad: (gltf) => {
          const mesh = gltf.scene
          mesh.scale.set(0.3, 0.3, 0.3)
          resolve(mesh)
        },
        onError: () => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(null)
        },
      })
    })
  }

  private setModelColor() {
    // 遍历所有网格并修改材质颜色
    this._mesh?.traverse((child) => {
    // 检查子对象是否为Mesh类型
      if (child instanceof Mesh) {
      // 现在TypeScript知道child是Mesh类型，拥有material属性
      // 检查material是否存在
        if (child.material) {
          if (child.material instanceof Material) {
          // 单一材质
            child.material = child.material.clone()
            child.material.color.set(this.color)
            // 自发光
            child.material.emissiveIntensity = 0.4
            child.material.emissive.set(this.color)
          }
          else if (Array.isArray(child.material)) {
          // 多重材质
            child.material = child.material.map((mat) => {
              mat.color.set(this.color)
              // 自发光
              mat.emissiveIntensity = 0.4
              mat.emissive.set(this.color)
            })
          }
        }
      }
    })
  }

  public async setupAnimation() {
    await this.ready

    const clock = new Clock()
    const animate = () => {
      this._animationController?.update(clock.getDelta())
      this._animationFrameId = requestAnimationFrame(animate)
    }

    this._disposeCallbacks.push(() => {
      cancelAnimationFrame(this._animationFrameId!)
    })

    animate()
  }
}

export default PyramidMesh